<script setup>
// NOOVI (cliente 2026-07-03): mostra TODOS os cards de Pipeline de um contato,
// em qualquer pipeline, na página do Contato. Espelha ContactAppointmentHistory
// e reaproveita o endpoint contact-scoped (pipeline_cards?contact_id=X) já usado
// por RelatedItemsTab.vue.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelinesAPI from 'dashboard/api/pipelines';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const cards = ref([]);
const pipelines = ref([]);
const loading = ref(false);
const error = ref(null);
const filterStatus = ref('all');

const pipelineById = computed(() =>
  pipelines.value.reduce((map, p) => ({ ...map, [p.id]: p }), {})
);

const filteredCards = computed(() => {
  if (filterStatus.value === 'all') return cards.value;
  return cards.value.filter(c => {
    if (filterStatus.value === 'won') return !!c.won_at;
    if (filterStatus.value === 'lost') return !!c.lost_at;
    if (filterStatus.value === 'open') return !c.won_at && !c.lost_at;
    return true;
  });
});

const statusBadgeClass = card => {
  if (card.won_at) return 'bg-grass-3 text-grass-11';
  if (card.lost_at) return 'bg-ruby-3 text-ruby-11';
  return 'bg-n-alpha-1 text-n-slate-11';
};

const statusLabel = card => {
  if (card.won_at) return t('PIPELINE.CARD.STATUS.WON');
  if (card.lost_at) return t('PIPELINE.CARD.STATUS.LOST');
  return t('PIPELINE.CARD.STATUS.OPEN');
};

const pipelineName = card =>
  pipelineById.value[card.pipeline_id]?.name || card.pipeline?.name || '—';

// Resolve stage names per card's OWN pipeline (cards span multiple pipelines,
// so there is no single "selected pipeline" to lean on here).
const stageName = card => {
  const pipeline = pipelineById.value[card.pipeline_id];
  return pipeline?.stages?.[card.pipeline_stage]?.name || card.pipeline_stage;
};

const formatRelativeTime = dateStr => {
  if (!dateStr) return '';
  try {
    const diffMins = Math.floor((Date.now() - new Date(dateStr)) / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    return `${Math.floor(diffHours / 24)}d`;
  } catch {
    return '';
  }
};

const generatePipelineSlug = pipeline => {
  if (!pipeline) return '';
  const name = pipeline.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${pipeline.id}-${name}`;
};

const goToCard = card => {
  const pipeline = pipelineById.value[card.pipeline_id];
  if (!pipeline) return;
  router.push({
    name: 'pipeline_item_detail',
    params: {
      accountId: route.params.accountId,
      pipelineSlug: generatePipelineSlug(pipeline),
      view: 'item',
      itemId: card.id,
    },
  });
};

const load = async () => {
  // Limpa antes de refazer: o Vue Router reutiliza esta instância ao navegar
  // entre contatos, então não podemos deixar os cards do contato anterior.
  cards.value = [];
  if (!props.contactId) return;

  loading.value = true;
  error.value = null;
  try {
    const [pipelinesRes, cardsRes] = await Promise.all([
      PipelinesAPI.get(),
      PipelineCardsAPI.getCards(null, {
        contact_id: props.contactId,
        limit: 50,
      }),
    ]);
    pipelines.value = pipelinesRes.data || [];
    const data = cardsRes.data;
    cards.value = data?.data ?? (Array.isArray(data) ? data : []);
  } catch {
    error.value = t('PIPELINE.RELATED.LOAD_ERROR');
    cards.value = [];
  } finally {
    loading.value = false;
  }
};

watch(() => props.contactId, load, { immediate: true });
</script>

<template>
  <div class="px-6">
    <div class="flex items-center justify-between gap-2 flex-wrap mb-3">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('PIPELINE.CONTACT_TAB.TITLE') }}
        <span v-if="cards.length" class="text-sm font-normal text-n-slate-11">
          ({{ cards.length }})
        </span>
      </h3>
      <select
        v-model="filterStatus"
        class="text-xs px-2 py-1 rounded border border-n-weak bg-n-solid-2 text-n-slate-11 focus:outline-none focus:border-n-brand"
      >
        <option value="all">{{ t('PIPELINE.RELATED.FILTER_ALL') }}</option>
        <option value="open">{{ t('PIPELINE.CARD.STATUS.OPEN') }}</option>
        <option value="won">{{ t('PIPELINE.CARD.STATUS.WON') }}</option>
        <option value="lost">{{ t('PIPELINE.CARD.STATUS.LOST') }}</option>
      </select>
    </div>

    <div v-if="error" class="text-xs text-ruby-11 py-4">{{ error }}</div>

    <div v-else-if="loading" class="text-center py-8">
      <span
        class="inline-block w-6 h-6 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
      />
    </div>

    <div
      v-else-if="!filteredCards.length"
      class="text-xs text-n-slate-11 italic py-4"
    >
      {{ t('PIPELINE.CONTACT_TAB.EMPTY') }}
    </div>

    <ul v-else class="space-y-2">
      <li
        v-for="card in filteredCards"
        :key="card.id"
        class="bg-n-solid-3 rounded-lg p-4 hover:bg-n-alpha-1 cursor-pointer transition-colors"
        @click="goToCard(card)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <h4 class="m-0 font-medium text-n-slate-12 truncate">
                {{ card.title || t('PIPELINE.UNTITLED_ITEM') }}
              </h4>
              <span
                class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full"
                :class="statusBadgeClass(card)"
              >
                {{ statusLabel(card) }}
              </span>
            </div>
            <div
              class="flex items-center gap-1.5 text-xs text-n-slate-11 flex-wrap"
            >
              <span>{{ pipelineName(card) }}</span>
              <span>•</span>
              <span>{{ stageName(card) }}</span>
            </div>
            <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
              <Avatar
                :name="card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')"
                :src="card.owner?.avatar_url"
                :size="20"
              />
              <span>{{
                card.owner?.name || t('PIPELINE.RELATED.NO_AGENT')
              }}</span>
              <span>•</span>
              <span>{{ formatRelativeTime(card.updated_at) }}</span>
            </div>
          </div>
          <button
            class="flex-shrink-0 px-3 py-1 text-xs font-medium bg-n-brand text-white rounded hover:brightness-110 transition-all"
            @click.stop="goToCard(card)"
          >
            {{ t('PIPELINE.RELATED.VIEW_ITEM') }}
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>
